import { supabase } from '../supabaseClient';
import { getIndexedDb, setIndexedDb } from './indexedDbStorage';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

export const PENDING_PHOTO_MARKER_PREFIX = 'pending_photo:';

export interface PendingQualityPhoto {
    id: string;
    protocolId: string;
    itemId: string;
    dataUrl: string; // Foto en base64 almacenada EXCLUSIVAMENTE en IndexedDB local
    marker: string;  // 'pending_photo:<id>' para savedProtocols (cero impacto en peso)
    createdAt: string;
    retryCount: number;
    collectionName?: string;
}

const STORAGE_KEY = 'klyd_pending_quality_photos';

/**
 * Determina si una referencia de foto corresponde a una foto pendiente de subida.
 */
export const isPendingPhotoMarker = (val?: string | null): boolean => {
    return typeof val === 'string' && val.startsWith(PENDING_PHOTO_MARKER_PREFIX);
};

/**
 * Convierte un DataURL a Blob binario para su subida a Supabase Storage.
 */
export const dataUrlToBlob = (dataUrl: string): Blob => {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
};

/**
 * Sube una fotografía directamente a Supabase Storage.
 */
export const uploadPhotoToStorage = async (fileOrBlob: File | Blob, protocolId: string, itemId: string): Promise<string> => {
    const extension = (fileOrBlob instanceof File && fileOrBlob.name) ? (fileOrBlob.name.split('.').pop() || 'jpg') : 'jpg';
    const sanitizeSegment = (s: string) => String(s || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9._\- ]/g, '_');
    const safeProtoId = sanitizeSegment(protocolId);
    const safeItemId = sanitizeSegment(itemId);
    const fileName = `quality_photos/${safeProtoId}_${safeItemId}_${Date.now()}.${extension}`;
    const { data, error } = await supabase.storage
        .from('graphic_plans')
        .upload(fileName, fileOrBlob, { cacheControl: '3600', upsert: true, contentType: 'image/jpeg' });

    if (error) {
        console.error('[PendingPhotosQueue] Falló subida a storage:', error);
        throw error;
    }
    return data.path;
};

/**
 * Guarda una fotografía en la cola local de IndexedDB del dispositivo.
 * Devuelve el registro de la foto pendiente con su marcador liviano.
 */
export const enqueuePendingPhoto = async (params: {
    protocolId: string;
    itemId: string;
    dataUrl: string;
    collectionName?: string;
}): Promise<PendingQualityPhoto> => {
    const id = `${params.protocolId}_${params.itemId}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    const marker = `${PENDING_PHOTO_MARKER_PREFIX}${id}`;
    let collectionName = params.collectionName;
    if (!collectionName && typeof localStorage !== 'undefined') {
        const selectedProjectId = localStorage.getItem('selectedProjectId');
        collectionName = selectedProjectId ? `project_${selectedProjectId}` : 'data';
    }
    const item: PendingQualityPhoto = {
        id,
        protocolId: params.protocolId,
        itemId: params.itemId,
        dataUrl: params.dataUrl,
        marker,
        createdAt: new Date().toISOString(),
        retryCount: 0,
        collectionName
    };

    const list = (await getIndexedDb<PendingQualityPhoto[]>(STORAGE_KEY)) || [];
    list.push(item);
    await setIndexedDb(STORAGE_KEY, list);
    return item;
};

/**
 * Obtiene todas las fotografías pendientes de subir desde IndexedDB.
 */
export const getPendingPhotos = async (): Promise<PendingQualityPhoto[]> => {
    return (await getIndexedDb<PendingQualityPhoto[]>(STORAGE_KEY)) || [];
};

/**
 * Elimina una fotografía de la cola local de IndexedDB.
 */
export const removePendingPhoto = async (idOrMarker: string): Promise<void> => {
    const id = idOrMarker.startsWith(PENDING_PHOTO_MARKER_PREFIX)
        ? idOrMarker.replace(PENDING_PHOTO_MARKER_PREFIX, '')
        : idOrMarker;
    const list = (await getIndexedDb<PendingQualityPhoto[]>(STORAGE_KEY)) || [];
    const updated = list.filter(p => p.id !== id && p.marker !== idOrMarker);
    await setIndexedDb(STORAGE_KEY, updated);
};

let isProcessingQueue = false;

export type OnPhotoUploadedCallback = (result: {
    protocolId: string;
    itemId: string;
    marker: string;
    storageUrl: string;
    collectionName?: string;
}) => void;

/**
 * Procesa la cola local de fotografías pendientes.
 * Intenta subir cada una a Supabase Storage y, al tener éxito:
 * 1. Lee la versión MÁS RECIENTE de savedProtocols desde Supabase justo antes de guardar.
 * 2. Reemplaza ÚNICAMENTE ese parámetro (marcador por URL de Storage).
 * 3. Actualiza atómicamente Supabase (evitando sobreescrituras desde estado de memoria stale).
 * 4. Borra la foto de IndexedDB.
 * 5. Notifica al callback para sincronizar la visualización en pantalla.
 */
export const processPendingPhotosQueue = async (
    onPhotoUploaded?: OnPhotoUploadedCallback
): Promise<number> => {
    if (isProcessingQueue) return 0;
    if (typeof window === 'undefined') return 0;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return 0;

    const photos = await getPendingPhotos();
    if (!photos || photos.length === 0) return 0;

    isProcessingQueue = true;
    let uploadedCount = 0;

    try {
        for (const photo of photos) {
            try {
                const blob = dataUrlToBlob(photo.dataUrl);
                const storageUrl = await uploadPhotoToStorage(blob, photo.protocolId, photo.itemId);
                if (storageUrl) {
                    // 1. Resolver colección de la obra (buscar exclusivamente en la obra de la foto)
                    let targetCollection = photo.collectionName;
                    if (!targetCollection && typeof localStorage !== 'undefined') {
                        const selectedProjectId = localStorage.getItem('selectedProjectId');
                        targetCollection = selectedProjectId ? `project_${selectedProjectId}` : 'data';
                    }

                    if (!targetCollection) {
                        console.warn(`[processPendingPhotosQueue] No se pudo determinar la colección para la foto ${photo.id}. Se conservará en IndexedDB.`);
                        continue;
                    }

                    let remotePayload: Record<string, any> | null = null;
                    try {
                        const { data: row, error: fetchErr } = await supabase
                            .from('app_data')
                            .select('payload')
                            .eq('collection_name', targetCollection)
                            .eq('doc_id', 'savedProtocols')
                            .maybeSingle();

                        if (fetchErr) {
                            console.warn(`[processPendingPhotosQueue] Error leyendo ${targetCollection}/savedProtocols:`, fetchErr);
                            continue;
                        }

                        if (row && row.payload && typeof row.payload === 'object') {
                            remotePayload = row.payload;
                        }
                    } catch (fetchDirectErr) {
                        console.warn('[processPendingPhotosQueue] Excepción leyendo savedProtocols:', fetchDirectErr);
                        continue;
                    }

                    const matchingKeys: string[] = [];
                    if (remotePayload) {
                        if (remotePayload[photo.protocolId]) {
                            matchingKeys.push(photo.protocolId);
                        }
                        for (const [k, protoVal] of Object.entries(remotePayload)) {
                            if (k === photo.protocolId || !protoVal || typeof protoVal !== 'object') continue;
                            const itemPhotos = (protoVal as any)?.checklist?.[photo.itemId]?.photos;
                            if (Array.isArray(itemPhotos) && itemPhotos.includes(photo.marker)) {
                                matchingKeys.push(k);
                            }
                        }
                    }

                    // IMPORTANTE: Buscar SOLO en la obra de la foto.
                    // Si el protocolo aún no existe en el documento remoto (aún no se sincroniza),
                    // CONSERVAR la foto en IndexedDB sin borrarla para reintentar en el próximo ciclo.
                    if (!remotePayload || matchingKeys.length === 0) {
                        console.warn(`[processPendingPhotosQueue] Protocolo ${photo.protocolId} aún no existe en ${targetCollection}. Conservando foto en cola local de IndexedDB.`);
                        continue;
                    }

                    // 2. Si encontramos la versión remota, actualizar atómicamente solo el parámetro afectado en todas sus llaves asociadas
                    matchingKeys.forEach(k => {
                        const targetProto = remotePayload![k];
                        if (targetProto) {
                            if (!targetProto.checklist) targetProto.checklist = {};
                            const targetItem = targetProto.checklist[photo.itemId];
                            if (targetItem) {
                                if (Array.isArray(targetItem.photos)) {
                                    targetItem.photos = targetItem.photos.map((p: string) => p === photo.marker ? storageUrl : p);
                                } else {
                                    targetItem.photos = [storageUrl];
                                }
                            }
                            targetProto.updatedAt = new Date().toISOString();
                        }
                    });
                    if (true) {

                        const { error: upsertErr } = await supabase
                            .from('app_data')
                            .upsert({
                                collection_name: targetCollection,
                                doc_id: 'savedProtocols',
                                payload: remotePayload,
                                updated_at: new Date().toISOString()
                            }, { onConflict: 'collection_name,doc_id' });

                        if (upsertErr) {
                            console.error('[processPendingPhotosQueue] Error guardando versión atómica en Supabase:', upsertErr);
                            throw upsertErr;
                        }
                    }

                    // 3. SOLO borrar de la cola local de IndexedDB tras confirmar guardado atómico exitoso
                    await removePendingPhoto(photo.id);
                    uploadedCount++;

                    // 4. Notificar a la UI en memoria para refrescar el render sin desfasar el estado
                    if (onPhotoUploaded) {
                        onPhotoUploaded({
                            protocolId: photo.protocolId,
                            itemId: photo.itemId,
                            marker: photo.marker,
                            storageUrl,
                            collectionName: targetCollection
                        });
                    }
                }
            } catch (itemErr) {
                console.warn(`[processPendingPhotosQueue] No se pudo subir foto pendiente ${photo.id}:`, itemErr);
            }
        }
    } finally {
        isProcessingQueue = false;
    }

    return uploadedCount;
};

/**
 * Registra los listeners automáticos para reintentar la subida al recuperar conexión o volver a la app.
 */
export const setupPendingPhotosSync = (
    onPhotoUploaded?: OnPhotoUploadedCallback
): (() => void) => {
    if (typeof window === 'undefined') return () => {};

    const handleOnline = () => {
        console.log('[PendingPhotosQueue] Conexión online detectada. Procesando cola de fotos pendientes...');
        processPendingPhotosQueue(onPhotoUploaded);
    };

    window.addEventListener('online', handleOnline);

    // Si estamos en entorno Capacitor nativo, suscribir también al evento appStateChange
    let appStateHandle: any = null;
    if (Capacitor.isNativePlatform()) {
        App.addListener('appStateChange', (state) => {
            if (state.isActive) {
                console.log('[PendingPhotosQueue] App reanudada. Procesando cola de fotos pendientes...');
                processPendingPhotosQueue(onPhotoUploaded);
            }
        }).then(h => {
            appStateHandle = h;
        }).catch(err => {
            console.warn('[PendingPhotosQueue] Error registrando appStateChange:', err);
        });
    }

    // Ejecución inicial al abrir la app
    processPendingPhotosQueue(onPhotoUploaded);

    return () => {
        window.removeEventListener('online', handleOnline);
        if (appStateHandle && typeof appStateHandle.remove === 'function') {
            appStateHandle.remove();
        }
    };
};
